import React, { useState } from "react";
import SectionLabel from "../../components/SectionLabel";
import "./FeedbackBoard.css";

const CATEGORY_COPY = {
  suggestion: { label: "Sugestão", tone: "accent" },
  problem: { label: "Problema", tone: "danger" },
  praise: { label: "Elogio", tone: "ok" },
  other: { label: "Outro", tone: "mute" },
};

const STATUS_COPY = {
  received: { label: "Recebida", tone: "received" },
  under_review: { label: "Em análise", tone: "review" },
  planned: { label: "Planejada", tone: "planned" },
  done: { label: "Entregue", tone: "done" },
};

const FILTERS = [
  { key: "most_voted", label: "Mais votados" },
  { key: "recent", label: "Recentes" },
  { key: "suggestion", label: "Sugestões" },
  { key: "problem", label: "Problemas" },
  { key: "done", label: "Entregues" },
];

const EMPTY_FORM = { category: "suggestion", title: "", body: "" };

export default function FeedbackBoard({
  stats: initialStats,
  feedbacks: initialFeedbacks = [],
  create_path: createPath,
}) {
  const [stats, setStats] = useState(initialStats);
  const [feedbacks, setFeedbacks] = useState(initialFeedbacks);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filter, setFilter] = useState("most_voted");
  const [busy, setBusy] = useState(false);
  const [votingId, setVotingId] = useState(null);
  const [errors, setErrors] = useState([]);
  const [sent, setSent] = useState(false);

  const visible = applyFilter(feedbacks, filter);

  const applyBoard = (data) => {
    setFeedbacks(data.feedbacks);
    setStats(data.stats);
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setErrors([]);
    setSent(false);
    try {
      const data = await postJson(createPath, { feedback: form });
      applyBoard(data);
      setForm(EMPTY_FORM);
      setSent(true);
    } catch (error) {
      setErrors(error.payload?.errors ?? ["Não foi possível enviar o feedback. Tente novamente."]);
    } finally {
      setBusy(false);
    }
  };

  const toggleVote = async (feedback) => {
    if (votingId) return;
    setVotingId(feedback.id);
    try {
      const data = await postJson(feedback.upvote_path, {});
      applyBoard(data);
    } catch {
      setErrors(["Não foi possível registrar o voto. Tente novamente."]);
    } finally {
      setVotingId(null);
    }
  };

  return (
    <main className="trunfo-feedback">
      <header className="trunfo-feedback__top">
        <SectionLabel tone="accent">SUA VOZ · CANAL ABERTO</SectionLabel>
        <h1 className="trunfo-feedback__title">
          Nos conte o que faria o Trunfo <em>decolar</em>
        </h1>
        <p className="trunfo-feedback__lede">
          Sugira funcionalidades, aponte problemas ou conte o que funcionou bem. As ideias mais
          votadas pela comunidade entram primeiro na fila de embarque.
        </p>
      </header>

      <dl className="trunfo-feedback__stats">
        <Stat value={stats.total} label="sugestões" />
        <Stat value={stats.planned} label="planejadas" />
        <Stat value={stats.done} label="entregues" />
      </dl>

      <div className="trunfo-feedback__layout">
        <form className="trunfo-feedback__ticket" onSubmit={submit} aria-label="Novo feedback">
          <div className="trunfo-feedback__ticket-header">
            <SectionLabel tone="accent">NOVO FEEDBACK</SectionLabel>
            <SectionLabel tone="mute">FB · {new Date().getFullYear()}</SectionLabel>
          </div>

          <div className="trunfo-feedback__ticket-body">
            <fieldset className="trunfo-feedback__field">
              <legend className="trunfo-feedback__label">Tipo</legend>
              <div className="trunfo-feedback__chips">
                {Object.entries(CATEGORY_COPY).map(([key, copy]) => (
                  <button
                    key={key}
                    type="button"
                    className={`trunfo-chip${form.category === key ? " trunfo-chip--active" : ""}`}
                    aria-pressed={form.category === key}
                    onClick={() => setForm({ ...form, category: key })}
                  >
                    {copy.label}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="trunfo-feedback__field">
              <label className="trunfo-feedback__label" htmlFor="feedback-title">
                Título
              </label>
              <input
                id="feedback-title"
                className="trunfo-feedback__input"
                type="text"
                maxLength={120}
                required
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
                placeholder="Resuma sua ideia em uma frase"
              />
            </div>

            <div className="trunfo-feedback__field">
              <label className="trunfo-feedback__label" htmlFor="feedback-body">
                Conte mais
              </label>
              <textarea
                id="feedback-body"
                className="trunfo-feedback__textarea"
                required
                minLength={20}
                maxLength={2000}
                value={form.body}
                onChange={(event) => setForm({ ...form, body: event.target.value })}
                placeholder="O que você gostaria de ver na plataforma? Quanto mais contexto, melhor."
              />
              <span className="trunfo-feedback__hint">mínimo 20 caracteres</span>
            </div>

            {errors.length > 0 ? (
              <ul className="trunfo-feedback__errors" role="alert">
                {errors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            ) : null}
            {sent ? (
              <p className="trunfo-feedback__sent" role="status">
                Feedback enviado. Obrigado por ajudar o Trunfo a decolar.
              </p>
            ) : null}
          </div>

          <div className="trunfo-feedback__ticket-stub">
            <span className="trunfo-feedback__stub-note">
              Você recebe um aviso quando o status mudar.
            </span>
            <button type="submit" className="trunfo-btn trunfo-btn--primary" disabled={busy}>
              {busy ? "Enviando..." : "Enviar feedback"}
            </button>
          </div>
        </form>

        <section className="trunfo-feedback__board" aria-label="Mural da comunidade">
          <SectionLabel as="h2" tone="mute">
            MURAL DA COMUNIDADE
          </SectionLabel>

          <div className="trunfo-feedback__filters" role="group" aria-label="Filtrar feedbacks">
            {FILTERS.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`trunfo-chip${filter === key ? " trunfo-chip--active" : ""}`}
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {visible.length === 0 ? (
            <p className="trunfo-feedback__empty">
              Nada por aqui ainda. Seja a primeira pessoa a sugerir algo.
            </p>
          ) : (
            <ul className="trunfo-feedback__list">
              {visible.map((feedback) => (
                <FeedbackCard
                  key={feedback.id}
                  feedback={feedback}
                  voting={votingId === feedback.id}
                  onVote={() => toggleVote(feedback)}
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}

function Stat({ value, label }) {
  return (
    <div className="trunfo-feedback__stat">
      <dt className="trunfo-feedback__stat-label">{label}</dt>
      <dd className="trunfo-feedback__stat-value">{value}</dd>
    </div>
  );
}

function FeedbackCard({ feedback, voting, onVote }) {
  const category = CATEGORY_COPY[feedback.category] ?? CATEGORY_COPY.other;
  const status = STATUS_COPY[feedback.status] ?? STATUS_COPY.received;
  return (
    <li className="trunfo-feedback__card">
      <div className="trunfo-feedback__votes">
        <button
          type="button"
          className={`trunfo-feedback__vote${feedback.voted ? " trunfo-feedback__vote--cast" : ""}`}
          aria-pressed={feedback.voted}
          aria-label={feedback.voted ? "Retirar voto" : "Votar nesta sugestão"}
          disabled={voting}
          onClick={onVote}
        >
          ▲
        </button>
        <span className="trunfo-feedback__vote-count">{feedback.votes}</span>
        <span className="trunfo-feedback__vote-label">votos</span>
      </div>
      <div className="trunfo-feedback__card-main">
        <div className="trunfo-feedback__card-meta">
          <span className={`trunfo-feedback__tag trunfo-feedback__tag--${category.tone}`}>
            {category.label}
          </span>
          <span className="trunfo-feedback__date">{formatDate(feedback.created_at)}</span>
          <span className={`trunfo-feedback__pill trunfo-feedback__pill--${status.tone}`}>
            {status.label}
          </span>
        </div>
        <h3 className="trunfo-feedback__card-title">{feedback.title}</h3>
        <p className="trunfo-feedback__card-body">{feedback.body}</p>
      </div>
    </li>
  );
}

function applyFilter(feedbacks, filter) {
  if (filter === "recent") {
    return [...feedbacks].sort((a, b) => b.created_at.localeCompare(a.created_at));
  }
  if (filter === "suggestion" || filter === "problem") {
    return feedbacks.filter((feedback) => feedback.category === filter);
  }
  if (filter === "done") {
    return feedbacks.filter((feedback) => feedback.status === "done");
  }
  return feedbacks;
}

async function postJson(url, body) {
  const meta = document.querySelector('meta[name="csrf-token"]');
  const response = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": meta ? meta.content : "",
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}`);
    error.payload = await response.json().catch(() => null);
    throw error;
  }
  return response.json();
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
